import { useCallback, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { IMAGES } from "@/data/content";

const BeforeAfterSlider = () => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);

  const update = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  }, []);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    update(e.clientX);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 4));
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="relative aspect-[16/10] w-full cursor-ew-resize select-none overflow-hidden border border-teal-line bg-panel touch-pan-y sm:aspect-[21/9]"
      onPointerDown={onPointerDown}
      onPointerMove={(e) => e.buttons === 1 && update(e.clientX)}
    >
      <img
        src={IMAGES.before}
        alt="Before — dust and water-spotted Audi S3 in the studio"
        draggable="false"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <span className="absolute bottom-4 left-4 z-10 bg-black/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-chrome">Before</span>

      <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img
          src={IMAGES.after}
          alt="After — the same Audi S3, gloss-black and spotless under studio lighting"
          draggable="false"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-mint/10" aria-hidden="true" />
      </div>
      <span className="absolute bottom-4 right-4 z-10 bg-black/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-mint" style={{ opacity: pos > 82 ? 0 : 1 }}>After</span>

      <div className="absolute inset-y-0 z-10" style={{ left: `${pos}%` }} aria-hidden="true">
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-mint" />
        <div className="absolute top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-mint bg-black text-mint">
          <MoveHorizontal className="h-5 w-5" />
        </div>
      </div>
      <input
        type="range"
        min="4"
        max="96"
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        onKeyDown={onKeyDown}
        aria-label="Reveal corrected paint comparison"
        data-testid="before-after-range"
        className="absolute inset-0 z-20 h-full w-full cursor-ew-resize opacity-0"
      />
    </div>
  );
};

export default BeforeAfterSlider;
